import React, { useState } from 'react'
import { ProvinceSelection, CommuneSelection } from '../../types/map.types'
import './SelectionSummary.styles.css'

interface SelectionSummaryProps {
  selectedProvinces: ProvinceSelection
  selectedCommunes: CommuneSelection
  isMapLoading?: boolean
  onProvinceRemove?: (provinceName: string) => void
}

export const SelectionSummary: React.FC<SelectionSummaryProps> = ({ 
  selectedProvinces, 
  selectedCommunes,
  isMapLoading = false,
  onProvinceRemove
}) => {
  const [expandedProvinces, setExpandedProvinces] = useState<Record<string, boolean>>({})
  
  const toggleProvinceExpanded = (provinceName: string) => {
    setExpandedProvinces(prev => ({
      ...prev,
      [provinceName]: !prev[provinceName]
    }))
  }
  
  const selectedProvincesList = Object.entries(selectedProvinces)
    .filter(([_, selected]) => selected)
    .map(([name]) => name)
  
  const getSelectedCommunesCount = (provinceName: string) => {
    const communes = selectedCommunes[provinceName] || {}
    return Object.values(communes).filter(Boolean).length
  }
  
  const getTotalCommunesCount = () => {
    return Object.values(selectedCommunes).reduce((total, communes) => {
      return total + Object.values(communes).filter(Boolean).length
    }, 0)
  }
  
  const getTotalCommunesInProvince = (provinceName: string) => {
    const communes = selectedCommunes[provinceName] || {}
    return Object.keys(communes).length
  }
  
  if (isMapLoading) {
    return (
      <div className="selection-summary loading">
        <div className="summary-header">
          <h3 className="summary-title">Sélection en cours</h3>
        </div>
        <div className="summary-loading">
          <div className="loading-spinner"></div>
          <p>Chargement des données géographiques...</p>
        </div>
      </div>
    )
  }
  
  return (
    <div className="selection-summary">
      <div className="summary-header">
        <h3 className="summary-title">Résumé de la sélection</h3>
        <div className="summary-stats">
          <span className="stat-item">
            <strong>{selectedProvincesList.length}</strong> province{selectedProvincesList.length !== 1 ? 's' : ''}
          </span>
          <span className="stat-separator">•</span>
          <span className="stat-item">
            <strong>{getTotalCommunesCount()}</strong> commune{getTotalCommunesCount() !== 1 ? 's' : ''}
          </span>
        </div>
      </div>
      
      {selectedProvincesList.length === 0 ? (
        <div className="empty-selection">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
            <circle cx="12" cy="10" r="3"></circle>
          </svg>
          <p>Aucune zone sélectionnée</p>
          <span className="empty-hint">Cliquez sur une province sur la carte pour commencer</span>
        </div>
      ) : (
        <div className="provinces-list">
          {selectedProvincesList.map(provinceName => {
            const communesCount = getSelectedCommunesCount(provinceName)
            const isExpanded = expandedProvinces[provinceName]
            const provinceCommunes = selectedCommunes[provinceName] || {}
            const selectedCommunesList = Object.entries(provinceCommunes)
              .filter(([_, selected]) => selected)
              .map(([name]) => name)
            
            return (
              <div key={provinceName} className="province-item">
                <div 
                  className="province-header"
                  onClick={() => toggleProvinceExpanded(provinceName)}
                >
                  <div className="province-info">
                    <span className="province-name">{provinceName}</span>
                    <span className="commune-count">
                      {communesCount > 0 
                        ? `${communesCount}/${getTotalCommunesInProvince(provinceName)} commune${communesCount !== 1 ? 's' : ''}` 
                        : 'Aucune commune sélectionnée'}
                    </span>
                  </div>
                  <div className="province-actions">
                    {onProvinceRemove && (
                      <button 
                        className="remove-btn"
                        onClick={(e) => {
                          e.stopPropagation()
                          onProvinceRemove(provinceName)
                        }}
                        title="Supprimer cette province"
                      >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </button>
                    )}
                    <button className="expand-btn">
                      <svg 
                        width="20" 
                        height="20" 
                        viewBox="0 0 20 20" 
                        fill="none"
                        style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
                      >
                        <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  </div>
                </div>
                
                {isExpanded && selectedCommunesList.length > 0 && (
                  <div className="communes-list">
                    {selectedCommunesList.map(communeName => (
                      <div key={communeName} className="commune-item">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <circle cx="8" cy="8" r="2" fill="#ef5343"/>
                        </svg>
                        <span>{communeName}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}